import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/cn'
import type { BlockDesk } from '@/features/quotes/components/useBlockDesk'
import type { QuoteBlock, QuoteBlockKind } from '@/types/domain'

/* The parts both block cards draw: one line, and the row that adds one.
 *
 * The desk itself — the reads, the writes and what a refusal says — is
 * `useBlockDesk`, and why there are two cards over one desk is answered there.
 *
 * ── Why there is no form sheet any more ─────────────────────────────────────
 *
 * There was one, and it asked three questions — a label, a body and a price
 * note — to add a line that reads "Customs duties and VAT at destination". A
 * modal is what you open to fill in a record; this is a list you type into. The
 * whole surface is in place now: the line is edited where it is read, and added
 * on the row beneath the last one.
 *
 * ── Why the delete is two presses and the second one has words ──────────────
 *
 * The resting state is the trash icon the design asks for, and pressing it does
 * NOT delete: it arms, and the armed state replaces the two icons with a
 * labelled "Delete" beside a Cancel. The design rules are explicit that a
 * destructive action always carries a text label, and a single-press icon
 * deleting a sentence that is going to a client is the one thing this list must
 * not do. Two presses, no modal, and the dangerous one is spelled out.
 */

/** Which list a line belongs to, for the colour only. The kind decides the
 *  behaviour; this decides whether the add control is green, red or plain. */
export type BlockTone = 'neutral' | 'positive' | 'negative'

const addButtonTone: Record<BlockTone, string> = {
  neutral: '',
  positive: 'text-positive border-positive/40 hover:bg-positive-soft',
  negative: 'text-negative border-negative/40 hover:bg-negative-soft',
}

export function BlockLine({
  block,
  desk,
  editLabel,
  deleteLabel,
}: {
  block: QuoteBlock
  desk: BlockDesk
  editLabel: string
  deleteLabel: string
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<string | null>(null)
  const [arming, setArming] = useState(false)

  const editing = draft !== null

  /* Past draft the row is text and nothing else. The server refuses the write
     either way (requireEditable), so a pencil that opens a field the save will
     bounce is a slower way to learn that — and the same rule the add rows
     already follow, applied to the line. */
  if (desk.readOnly) {
    return (
      <li className="border-line-hair flex items-center gap-2 border-b py-2 last:border-b-0">
        <BlockBody block={block} />
      </li>
    )
  }

  if (editing) {
    return (
      <li className="border-line-hair flex items-center gap-2 border-b py-2 last:border-b-0">
        <Input
          value={draft}
          inputSize="sm"
          autoFocus
          aria-label={editLabel}
          className="min-w-0 flex-1"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && draft.trim()) {
              event.preventDefault()
              desk.amend(block, draft, () => setDraft(null))
            }
            if (event.key === 'Escape') {
              event.preventDefault()
              setDraft(null)
            }
          }}
        />
        {/* Committing an edit is not destructive, so it may be an icon. */}
        <IconButton
          icon={Check}
          size="sm"
          label={t('common.actions.save')}
          disabled={!draft.trim() || desk.pending}
          onClick={() => desk.amend(block, draft, () => setDraft(null))}
        />
        <IconButton
          icon={X}
          size="sm"
          label={t('common.actions.cancel')}
          onClick={() => setDraft(null)}
        />
      </li>
    )
  }

  return (
    <li className="border-line-hair flex items-center gap-2 border-b py-2 last:border-b-0">
      <BlockBody block={block} />

      {arming ? (
        <span className="flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setArming(false)}
            disabled={desk.pending}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => desk.remove(block)}
            disabled={desk.pending}
          >
            {t('quotes.blocks.delete')}
          </Button>
        </span>
      ) : (
        <span className="flex shrink-0 items-center gap-1">
          <IconButton
            icon={Pencil}
            size="sm"
            label={editLabel}
            onClick={() => setDraft(block.body)}
          />
          {/* Arms only. The press that deletes is the labelled one above. */}
          <IconButton
            icon={Trash2}
            size="sm"
            label={deleteLabel}
            onClick={() => setArming(true)}
          />
        </span>
      )}
    </li>
  )
}

/** The printed half of a line, shared by the editable row and the frozen one so
 *  the two cannot disagree about what a stored price note looks like. */
function BlockBody({ block }: { block: QuoteBlock }) {
  return (
    <span className="text-text-body min-w-0 flex-1 text-[13px] leading-snug">
      {block.body}
      {/* An empty price note renders NOTHING — not a dash, not a zero. Any
          placeholder there reads as a quoted amount (AC-3), and the screen must
          not say something the document deliberately does not. The em dash is
          the composer's own join, so the row reads as the paper will. */}
      {block.priceText ? (
        <span className="text-text-muted"> — {block.priceText}</span>
      ) : null}
    </span>
  )
}

/** The row beneath the last line. `compact` is the square + the two-column
 *  panels use; the notes card gives its action a word, because it stands alone
 *  at the foot of a card rather than inside a tinted panel that names it. */
export function BlockAddRow({
  desk,
  kind,
  placeholder,
  actionLabel,
  tone = 'neutral',
  compact = false,
}: {
  desk: BlockDesk
  kind: QuoteBlockKind
  placeholder: string
  actionLabel: string
  tone?: BlockTone
  compact?: boolean
}) {
  const [draft, setDraft] = useState('')

  /* Cleared from the SUCCESS callback alone: a refusal — the kind is full, the
     quote moved past draft — must leave the sentence on screen to be retried,
     which is the same bargain the sheet used to make. */
  const submit = () => {
    if (!draft.trim() || desk.pending) return
    desk.create(kind, draft, () => setDraft(''))
  }

  return (
    <div className="flex items-center gap-2 pt-1">
      <Input
        value={draft}
        inputSize="sm"
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            submit()
          }
        }}
      />
      {compact ? (
        <IconButton
          icon={Plus}
          label={actionLabel}
          disabled={!draft.trim() || desk.pending}
          onClick={submit}
          className={cn('border', addButtonTone[tone])}
        />
      ) : (
        <Button size="sm" onClick={submit} disabled={!draft.trim() || desk.pending}>
          {actionLabel}
        </Button>
      )}
    </div>
  )
}

/** A refusal has nowhere else to go now that there is no sheet to carry it. */
export function BlockDeskError({ desk }: { desk: BlockDesk }) {
  if (!desk.failureText) return null

  return (
    <p role="alert" className="text-negative text-[12.5px] leading-snug">
      {desk.failureText}
    </p>
  )
}

/** Said as a line rather than as an EmptyState: these lists sit inside a card,
 *  and a centred icon block would outweigh the list it stands in for. Nothing is
 *  wrong here — most quotes exclude nothing — so it must not read as a gap. */
export function BlockListEmpty({ children }: { children: string }) {
  return (
    <p className="text-text-muted py-2 text-[12.5px] leading-snug font-light">
      {children}
    </p>
  )
}
